import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { reportsAPI } from '../../services/api';
import useStore from '../../store/useStore';
import { displayRequirements, formToReport } from '../../utils/cubeQuery';
import useReportResult from '../../hooks/useReportResult';
import {
  buildCatalogue, reachMap, fieldStatus, selectionCubes, canCombine, joinsInUse, cubesInUse, suggestTitle,
  recommendDisplay, formFromReport, normalizePivot, missingMembers, withoutMember, moveItem,
} from './reportModel';
import FieldBrowser from './FieldBrowser';
import BuildPanel from './BuildPanel';
import FormatPanel from './FormatPanel';
import PreviewCanvas from './PreviewCanvas';
import RelationshipMap from './RelationshipMap';
import { IconBack, IconDatabase, IconWarn, IconClose } from './icons';
import '../../styles/ReportEditor.css';

const PREVIEW_LIMIT = 200;
const PREVIEW_DELAY_MS = 400;

const errorText = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

/** The members a shelf holds, for moving chips between shelves. */
const SHELF_LIST = { values: 'measures', groups: 'dimensions' };

/**
 * Full-screen report editor. Fields come from every dataset the company has
 * (and live metric histories); the Data panel only offers fields that can be
 * joined to the current selection. The preview runs the report as it will be
 * saved, including the previous-period comparison.
 *
 * Props match the earlier modal builder: onCreate(report) for a new report,
 * onUpdate(editingWidget, report) when editingWidget is given (a widget with a
 * saved `report`, or an older widget with only a `config`). initialReport
 * starts a new report from a spec, such as one the assistant drafted.
 */
const ReportEditor = ({ onClose, onCreate, onUpdate, editingWidget = null, initialReport = null }) => {
  const currentCompany = useStore((state) => state.currentCompany);
  const user = useStore((state) => state.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const companyId = currentCompany?.id;
  const isEditMode = !!editingWidget;
  const savedReport = editingWidget?.report || null;
  const legacyConfig = isEditMode && !savedReport && !editingWidget.config?.cubeQuery;

  const initial = useMemo(() => normalizePivot(
    savedReport ? formFromReport(savedReport)
      : isEditMode ? formFromReport(editingWidget.config || {}, { defaultVisibility: 'company' })
        : initialReport ? formFromReport({ ...initialReport, visibility: initialReport.visibility || (isAdmin ? 'company' : 'private') })
        : formFromReport(null, { defaultVisibility: isAdmin ? 'company' : 'private' })
  ), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [form, setFormState] = useState(initial);
  const initialJson = useRef(JSON.stringify(initial));
  // Until the display type is chosen by hand, it follows the selection.
  const displayChosen = useRef(isEditMode);

  const [meta, setMeta] = useState({ loading: true, error: null, cubes: [] });
  const [library, setLibrary] = useState([]);
  const [rightTab, setRightTab] = useState('build');
  const [openFilter, setOpenFilter] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [dataOpen, setDataOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dragRef = useRef(null);
  const titleRef = useRef(null);

  // ---- Metadata and template reports -----------------------------------
  useEffect(() => {
    if (!companyId) return undefined;
    let live = true;
    (async () => {
      try {
        const res = await reportsAPI.getMeta(companyId);
        if (!live) return;
        if (res?.success) setMeta({ loading: false, error: null, cubes: res.data?.cubes || [] });
        else setMeta({ loading: false, error: res?.error || 'Could not load the reporting metadata', cubes: [] });
      } catch (err) {
        if (live) setMeta({ loading: false, error: errorText(err, 'Could not load the reporting metadata'), cubes: [] });
      }
    })();
    if (!isEditMode) {
      Promise.resolve(reportsAPI.library?.(companyId))
        .then((res) => { if (live && res?.success) setLibrary(res.data?.reports || []); })
        .catch(() => {});
    }
    return () => { live = false; };
  }, [companyId, isEditMode]);

  const catalogue = useMemo(() => buildCatalogue(meta.cubes), [meta.cubes]);
  const reach = useMemo(() => reachMap(catalogue.cubes), [catalogue]);

  // ---- Form updates ----------------------------------------------------
  const setForm = useCallback((updater) => {
    setFormState((prev) => {
      let next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      if (!displayChosen.current) next = { ...next, displayType: recommendDisplay(next) };
      return normalizePivot(next);
    });
  }, []);

  const statusOf = useCallback((field) => fieldStatus(field, form, catalogue, reach), [form, catalogue, reach]);

  const placeField = (f, field, shelf, index) => {
    let next = withoutMember(f, field.name);
    // A field already shown keeps its sort when it moves.
    if (f.orderMember === field.name) next.orderMember = field.name;
    if (shelf === 'time') {
      next.timeDimension = field.name;
      // A report already grouped by something, such as a bar per account, most likely wants
      // the date only to limit the period, so it is broken down by month only when the
      // date would be the chart's axis or a time chart was chosen.
      const chosen = displayChosen.current ? next.displayType : null;
      const overTime = chosen ? ['line', 'area'].includes(chosen) : next.dimensions.length === 0;
      next.granularity = f.timeDimension === field.name ? f.granularity
        : (f.granularity || (overTime ? 'month' : ''));
      next.dateRange = f.timeDimension === field.name ? f.dateRange : (f.dateRange || '');
      return next;
    }
    const key = SHELF_LIST[shelf];
    const list = [...next[key]];
    list.splice(index === undefined ? list.length : Math.min(index, list.length), 0, field.name);
    return { ...next, [key]: list };
  };

  const actions = useMemo(() => ({
    update: (patch) => setForm(patch),
    add: (field, shelf, index) => setForm((f) => placeField(f, field, shelf, index)),
    remove: (name) => setForm((f) => withoutMember(f, name)),
    drop: (payload, shelf, index) => setForm((f) => {
      const { field, from } = payload;
      if (from === shelf && SHELF_LIST[shelf]) {
        const key = SHELF_LIST[shelf];
        const fromIndex = f[key].indexOf(field.name);
        if (fromIndex < 0) return f;
        const to = index > fromIndex ? index - 1 : index;
        return { ...f, [key]: moveItem(f[key], fromIndex, Math.min(to, f[key].length - 1)) };
      }
      if (from === shelf) return f;
      return placeField(f, field, shelf, index);
    }),
    addFilter: (field) => {
      setForm((f) => ({ ...f, filters: [...f.filters, { member: field.name, operator: 'equals', values: [] }] }));
      setOpenFilter(form.filters.length);
      setRightTab('build');
    },
    updateFilter: (i, next) => setForm((f) => ({ ...f, filters: f.filters.map((x, j) => (j === i ? next : x)) })),
    removeFilter: (i) => {
      setForm((f) => ({ ...f, filters: f.filters.filter((_, j) => j !== i) }));
      setOpenFilter(null);
    },
  }), [setForm, form.filters.length]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Click in the Data panel: add to the natural shelf, or remove. */
  const toggleField = useCallback((field) => {
    setForm((f) => {
      const selected = f.measures.includes(field.name) || f.dimensions.includes(field.name) || f.timeDimension === field.name;
      if (selected) return withoutMember(f, field.name);
      if (field.kind === 'measure') return placeField(f, field, 'values');
      if (field.kind === 'time' && !f.timeDimension) return placeField(f, field, 'time');
      return placeField(f, field, 'groups');
    });
  }, [setForm]); // eslint-disable-line react-hooks/exhaustive-deps

  const setDisplayType = (id) => {
    displayChosen.current = true;
    setForm((f) => ({ ...f, displayType: id }));
  };

  const applyTemplate = (r) => {
    displayChosen.current = true;
    setFormState(normalizePivot({
      ...formFromReport({ title: r.title, displayType: r.displayType, cubeQuery: r.cubeQuery, refreshMinutes: r.refreshMinutes },
        { defaultVisibility: form.visibility }),
    }));
  };

  // ---- Derived state ---------------------------------------------------
  const hasSelection = form.measures.length > 0 || form.dimensions.length > 0 || !!form.timeDimension;
  const isEmpty = !hasSelection && form.filters.length === 0;
  const autoTitle = useMemo(() => suggestTitle(form, catalogue), [form, catalogue]);
  const title = form.title.trim() || autoTitle || 'Untitled report';
  const missing = useMemo(() => (meta.loading ? [] : missingMembers(form, catalogue.fields)), [form, catalogue, meta.loading]);
  const combinable = useMemo(() => {
    const { measureCubes, dimensionCubes } = selectionCubes(form, catalogue.fields);
    return canCombine(reach, measureCubes, dimensionCubes);
  }, [form, catalogue, reach]);
  let invalidReason = null;
  if (missing.length) invalidReason = `These fields are no longer available: ${missing.join(', ')}. Remove them to continue.`;
  else if (hasSelection && !combinable) invalidReason = 'The selected fields come from datasets that are not related. Remove fields until the Data panel shows no conflicts.';
  else if (!hasSelection && form.filters.length) invalidReason = 'Add a value or a group to show results.';
  const joins = useMemo(() => (combinable ? joinsInUse(form, catalogue, reach) : []), [form, catalogue, reach, combinable]);
  const inUse = useMemo(() => cubesInUse(form, catalogue.fields), [form, catalogue]);
  const requirements = displayRequirements(form);
  const recommended = recommendDisplay(form);
  const report = useMemo(() => ({ ...formToReport(form), name: title, description: form.description?.trim() || null }), [form, title]);
  const previewKey = JSON.stringify([report.cubeQuery, report.display]);
  const canPreview = hasSelection && !invalidReason && !meta.loading;
  const dirty = JSON.stringify(form) !== initialJson.current;

  // ---- Live preview ----------------------------------------------------
  // The preview runs the report as it will be saved, a moment after each change.
  const draft = useMemo(() => (canPreview ? { cubeQuery: report.cubeQuery, display: report.display } : null), [canPreview, previewKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const preview = useReportResult({ companyId, report: draft, limit: PREVIEW_LIMIT, delayMs: PREVIEW_DELAY_MS, enabled: Boolean(draft) });

  // ---- Value suggestions and SQL ---------------------------------------
  const valueCache = useRef(new Map());
  const loadValues = useCallback(async (member) => {
    if (valueCache.current.has(member)) return valueCache.current.get(member);
    const res = await reportsAPI.query(companyId, { dimensions: [member], limit: 500, order: { [member]: 'asc' } });
    if (!res?.success) throw new Error(res?.error || 'Could not load values');
    const values = [...new Set((res.data?.data || []).map((r) => r[member]).filter((v) => v !== null && v !== undefined).map(String))];
    valueCache.current.set(member, values);
    return values;
  }, [companyId]);

  const loadSql = useCallback(async () => {
    const res = await reportsAPI.sql(companyId, report.cubeQuery);
    if (!res?.success) throw new Error(res?.error || 'Could not generate SQL');
    const sql = res.data?.sql;
    if (typeof sql === 'string') return sql;
    if (Array.isArray(sql?.sql)) return sql.sql[0];
    return JSON.stringify(sql, null, 2);
  }, [companyId, previewKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Save and close --------------------------------------------------
  const canSave = hasSelection && !invalidReason && !saving && !meta.loading && (!isEditMode || dirty || legacyConfig);

  const save = useCallback(async () => {
    if (!canSave) return;
    setError('');
    setSaving(true);
    try {
      if (isEditMode) await onUpdate(editingWidget, report);
      else await onCreate(report);
      initialJson.current = JSON.stringify(form);
    } catch (err) {
      setError(errorText(err, isEditMode ? 'Could not save the report' : 'Could not create the report'));
    } finally {
      setSaving(false);
    }
  }, [canSave, isEditMode, onUpdate, onCreate, editingWidget, report, form]);

  const requestClose = useCallback(() => {
    if (dirty && !window.confirm('Discard the unsaved changes to this report?')) return;
    onClose();
  }, [dirty, onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); save(); return; }
      if (e.key === 'Escape' && !e.defaultPrevented && !showMap) {
        const tag = e.target?.tagName;
        if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
        if (openFilter !== null) { setOpenFilter(null); return; }
        requestClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [save, requestClose, showMap, openFilter]);

  useEffect(() => {
    if (!dirty) return undefined;
    const onUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ---- Render ----------------------------------------------------------
  const editor = (
    <div className="rf-editor" role="dialog" aria-modal="true" aria-labelledby="rf-editor-heading">
      <header className="rf-topbar">
        <button type="button" className="rf-icon-btn rf-back" onClick={requestClose} aria-label="Close the report editor" title="Close">
          <IconBack size={18} />
        </button>
        <div className="rf-title-block">
          <h2 id="rf-editor-heading" className="rf-eyebrow">{isEditMode ? 'Edit report' : 'New report'}</h2>
          <input
            ref={titleRef}
            className="rf-title-input"
            aria-label="Report title"
            value={form.title}
            placeholder={autoTitle || 'Untitled report'}
            onChange={(e) => setForm({ title: e.target.value })}
            maxLength={200}
          />
        </div>
        <span className={`rf-save-state ${dirty ? 'is-dirty' : ''}`}>{dirty ? 'Unsaved changes' : isEditMode ? 'No changes' : ''}</span>
        <div className="rf-topbar-actions">
          <button type="button" className="rf-btn rf-btn--ghost rf-data-toggle" onClick={() => setDataOpen((v) => !v)} aria-expanded={dataOpen}>
            <IconDatabase size={15} /> Data
          </button>
          <button type="button" className="rf-btn rf-btn--ghost" onClick={requestClose}>Cancel</button>
          <button type="button" className="rf-btn rf-btn--primary" onClick={save} disabled={!canSave} title="Save (Ctrl+S)">
            {saving ? 'Saving…' : isEditMode ? 'Save changes' : 'Create report'}
          </button>
        </div>
      </header>

      {(error || meta.error || legacyConfig) && (
        <div className="rf-banners">
          {error && <div className="rf-banner rf-banner--error" role="alert"><IconWarn size={15} /> {error}<button type="button" className="rf-icon-btn" aria-label="Dismiss" onClick={() => setError('')}><IconClose size={13} /></button></div>}
          {meta.error && <div className="rf-banner rf-banner--error" role="alert"><IconWarn size={15} /> {meta.error}</div>}
          {legacyConfig && (
            <div className="rf-banner rf-banner--warn">
              <IconWarn size={15} /> This widget uses the earlier single-dataset query. Choose its fields again to convert it; saving replaces the old query with a saved report.
            </div>
          )}
        </div>
      )}

      <div className="rf-body">
        <aside className={`rf-pane rf-pane--left ${dataOpen ? 'is-open' : ''}`} aria-label="Data">
          {meta.loading ? <div className="rf-pane-loading"><span className="rf-spinner" /> Loading datasets…</div> : (
            <FieldBrowser catalogue={catalogue} form={form} statusOf={statusOf} onToggle={toggleField} dragRef={dragRef} onShowMap={() => setShowMap(true)} />
          )}
        </aside>

        <main className="rf-pane rf-pane--center">
          <PreviewCanvas
            form={form}
            title={title}
            recommended={hasSelection ? recommended : null}
            requirements={requirements}
            preview={preview}
            previewLimit={PREVIEW_LIMIT}
            onDisplayType={setDisplayType}
            onRefresh={preview.run}
            joins={joins}
            catalogue={catalogue}
            library={library}
            onUseTemplate={applyTemplate}
            loadSql={loadSql}
            queryKey={previewKey}
            isEmpty={isEmpty}
            invalidReason={invalidReason}
          />
        </main>

        <aside className="rf-pane rf-pane--right" aria-label="Report settings">
          <div className="rf-tabs" role="tablist" aria-label="Report settings">
            {[['build', 'Build'], ['format', 'Format']].map(([id, label]) => (
              <button key={id} type="button" role="tab" id={`rf-tab-${id}`} aria-selected={rightTab === id} aria-controls={`rf-tabpanel-${id}`}
                className={rightTab === id ? 'is-on' : ''} onClick={() => setRightTab(id)}>{label}</button>
            ))}
          </div>
          <div className="rf-right-scroll" role="tabpanel" id={`rf-tabpanel-${rightTab}`} aria-labelledby={`rf-tab-${rightTab}`}>
            {rightTab === 'build' ? (
              <BuildPanel form={form} catalogue={catalogue} statusOf={statusOf} dragRef={dragRef} actions={actions}
                openFilter={openFilter} setOpenFilter={setOpenFilter} loadValues={loadValues} />
            ) : (
              <FormatPanel form={form} catalogue={catalogue} isAdmin={isAdmin} update={actions.update} />
            )}
          </div>
        </aside>
      </div>

      {showMap && <RelationshipMap catalogue={catalogue} inUse={inUse} joinsUsed={joins} onClose={() => setShowMap(false)} />}
    </div>
  );

  return createPortal(editor, document.body);
};

export default ReportEditor;
